import React, { useEffect, useState } from 'react';
import { SystemSetting, UserInfo } from '../../api/admin';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminOperations } from '../../hooks/useAdminOperations';
import CreateUserModal from '../modals/CreateUserModal';
import DeleteConfirmationModal from '../modals/DeleteConfirmationModal';
import EditSystemSettingModal from '../modals/EditSystemSettingModal';
import EditUserModal from '../modals/EditUserModal';
import SystemSettingsTable from '../tables/SystemSettingsTable';
import UserTable from '../tables/UserTable';
import Button from '../ui/Button';

const AdminPanel: React.FC = () => {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [settings, setSettings] = useState<SystemSetting[]>([]);

  // Modal states
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [editingUser, setEditingUser] = useState<UserInfo | null>(null);
  const [editingSetting, setEditingSetting] = useState<SystemSetting | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserInfo | null>(null);

  const { user } = useAuth();
  const { loadAdminData: loadData, deleteUser: deleteUserAPI, showToast } = useAdminOperations();

  useEffect(() => {
    loadData().then((result) => {
      if (result) {
        setUsers(result.users);
        setSettings(result.settings);
      }
    });
  }, [loadData]);

  const refreshData = async () => {
    const result = await loadData();
    if (result) {
      setUsers(result.users);
      setSettings(result.settings);
    }
  };

  const handleCreateSuccess = async () => {
    await refreshData();
  };

  const handleEditSuccess = async () => {
    await refreshData();
    setEditingUser(null);
  };

  const handleEditSetting = (setting: SystemSetting) => {
    setEditingSetting(setting);
  };

  const handleEditSettingSuccess = async () => {
    await refreshData();
    setEditingSetting(null);
  };

  const handleDeleteUser = (userToDelete: UserInfo) => {
    // Prevent deleting yourself
    if (userToDelete.username === user?.username) {
      showToast('You cannot delete your own account', 'error');
      return;
    }

    setUserToDelete(userToDelete);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    const result = await deleteUserAPI(userToDelete.id);
    if (result) {
      await refreshData();
      setUserToDelete(null);
    }
  };

  return (
    <div>
      {/* Users Management */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-[#0C233E]">Users ({users.length})</h3>
          <Button variant="primary" onClick={() => setShowCreateUser(true)}>
            Create User
          </Button>
        </div>

        <UserTable users={users} onEditUser={setEditingUser} onDeleteUser={handleDeleteUser} currentUsername={user?.username} />
      </div>

      {/* System Settings Details */}
      {settings.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-[#0C233E] mb-4">System Configuration</h3>

          <SystemSettingsTable settings={settings} onEditSetting={handleEditSetting} />
        </div>
      )}

      {/* Modals */}
      <CreateUserModal
        isOpen={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        onSuccess={handleCreateSuccess}
        onError={(message) => showToast(message, 'error')}
      />

      <EditUserModal
        isOpen={!!editingUser}
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onSuccess={handleEditSuccess}
        onError={(message) => showToast(message, 'error')}
      />

      <EditSystemSettingModal
        isOpen={!!editingSetting}
        setting={editingSetting}
        onClose={() => setEditingSetting(null)}
        onSuccess={handleEditSettingSuccess}
      />

      <DeleteConfirmationModal isOpen={!!userToDelete} user={userToDelete} onClose={() => setUserToDelete(null)} onConfirm={handleConfirmDelete} />
    </div>
  );
};

export default AdminPanel;
